import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { catchError, tap } from 'rxjs/operators';
import { HttpUtilService } from './http-util.service';
import { NotificationsService } from './notifications.service';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class CognitoDomainService {
  private _cognitoDomainState = new BehaviorSubject({
    isLoading: true,
    cognitoDomain: null,
  });

  private _cognitoSsoUrl = new BehaviorSubject<string | null>(null);
  private hasAttemptedCognitoDomain: boolean = false;

  constructor(
    private notifications: NotificationsService,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private translationService: TranslationService
  ) {
    this.initializeSsoUrl();
  }

  private initializeSsoUrl() {
    try {
      // Check if the script is already loaded
      const scriptElement = document.querySelector(
        'script[src="/school-assets/cogntio-domain.js"]'
      );

      if (scriptElement) {
        // Script element exists, check if it's loaded
        if (
          scriptElement.getAttribute('data-loaded') === 'true' ||
          window?.['scaffoldSSODomain']
        ) {
          // Script is already loaded
          this.checkAndSetSsoUrl();
        } else {
          // Script exists but not loaded yet, wait for load event
          try {
            scriptElement.addEventListener('load', () => {
              console.log('CognitoDomainService: External script loaded');
              this.checkAndSetSsoUrl();
            });
          } catch (error) {
            console.warn(
              'CognitoDomainService: Could not add load listener, using fallback'
            );
            this.useFallbackDetection();
          }
        }
      } else {
        // Script element doesn't exist yet, use MutationObserver to watch for it
        this.watchForScriptLoad();
      }
    } catch (error) {
      console.warn(
        'CognitoDomainService: Error in initializeSsoUrl, using fallback:',
        error
      );
      this.useFallbackDetection();
    }
  }

  private watchForScriptLoad() {
    try {
      if (!window.MutationObserver) {
        console.warn(
          'CognitoDomainService: MutationObserver not available, using fallback'
        );
        this.useFallbackDetection();
        return;
      }

      const observer = new MutationObserver(mutations => {
        try {
          mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.tagName === 'SCRIPT' &&
                  element.getAttribute('src')?.includes('cogntio-domain.js')
                ) {
                  console.log(
                    'CognitoDomainService: Found script element, waiting for load'
                  );
                  try {
                    element.addEventListener('load', () => {
                      console.log(
                        'CognitoDomainService: External script loaded via observer'
                      );
                      this.checkAndSetSsoUrl();
                      observer.disconnect();
                    });
                  } catch (error) {
                    console.warn(
                      'CognitoDomainService: Could not add load listener to observed script'
                    );
                  }
                }
              }
            });
          });
        } catch (error) {
          console.warn(
            'CognitoDomainService: Error in mutation observer:',
            error
          );
        }
      });

      try {
        observer.observe(document.head, { childList: true, subtree: true });
      } catch (error) {
        console.warn(
          'CognitoDomainService: Could not observe document.head, using fallback'
        );
        this.useFallbackDetection();
        return;
      }

      // Also check periodically as a fallback
      setTimeout(() => {
        try {
          if (window?.['scaffoldSSODomain']) {
            console.log(
              'CognitoDomainService: SSO URL found via fallback check'
            );
            this.checkAndSetSsoUrl();
            observer.disconnect();
          }
        } catch (error) {
          console.warn('CognitoDomainService: Error in fallback check:', error);
        }
      }, 1000);

      // Use DOMContentLoaded as another fallback
      try {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            console.log('CognitoDomainService: DOMContentLoaded event fired');
            this.checkAndSetSsoUrl();
            observer.disconnect();
          });
        } else {
          // DOM is already loaded, check immediately
          this.checkAndSetSsoUrl();
        }
      } catch (error) {
        console.warn(
          'CognitoDomainService: Error with DOMContentLoaded listener:',
          error
        );
      }
    } catch (error) {
      console.warn(
        'CognitoDomainService: Error in watchForScriptLoad, using fallback:',
        error
      );
      this.useFallbackDetection();
    }
  }

  private useFallbackDetection() {
    try {
      // Simple fallback: just check periodically
      const checkInterval = setInterval(() => {
        try {
          if (window?.['scaffoldSSODomain']) {
            console.log(
              'CognitoDomainService: SSO URL found via simple fallback'
            );
            this.checkAndSetSsoUrl();
            clearInterval(checkInterval);
          }
        } catch (error) {
          console.warn(
            'CognitoDomainService: Error in fallback interval:',
            error
          );
        }
      }, 500);

      // Stop checking after 10 seconds to avoid infinite loops
      setTimeout(() => {
        try {
          clearInterval(checkInterval);
        } catch (error) {
          console.warn(
            'CognitoDomainService: Error clearing fallback interval:',
            error
          );
        }
      }, 10000);
    } catch (error) {
      console.warn(
        'CognitoDomainService: Error setting up fallback detection:',
        error
      );
    }
  }

  private checkAndSetSsoUrl() {
    try {
      const ssoUrl = window?.['scaffoldSSODomain']?.['cognitoSsoUrl'];
      console.log('CognitoDomainService: Checking for SSO URL:', ssoUrl);
      if (ssoUrl) {
        console.log(
          'CognitoDomainService: Setting SSO URL from window object:',
          ssoUrl
        );
        this._cognitoSsoUrl.next(ssoUrl);
      }
    } catch (error) {
      console.warn(
        'CognitoDomainService: Error checking/setting SSO URL:',
        error
      );
    }
  }

  getCognitoSsoUrl$() {
    try {
      // Also check for SSO URL dynamically when requested
      const ssoUrl =
        window?.['scaffoldSSODomain']?.['cognitoSsoUrl'] || '';
      if (ssoUrl && this._cognitoSsoUrl.value !== ssoUrl) {
        console.log(
          'CognitoDomainService: Setting SSO URL from dynamic check:',
          ssoUrl
        );
        this._cognitoSsoUrl.next(ssoUrl);
      }
    } catch (error) {
      console.warn('CognitoDomainService: Error in getCognitoSsoUrl$:', error);
    }
    return this._cognitoSsoUrl.asObservable();
  }

  /**
   * Manually trigger SSO URL detection
   * Useful for cases where the external script loads after the service is initialized
   */
  refreshSsoUrl() {
    try {
      console.log('CognitoDomainService: Manually refreshing SSO URL');
      this.checkAndSetSsoUrl();
    } catch (error) {
      console.warn('CognitoDomainService: Error in refreshSsoUrl:', error);
    }
  }

  updateCognitoSsoUrl() {
    this.http
      .request(
        'POST',
        this.environmentService.environment.apiRoot + '/cognito-domain'
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap(response => {
          if (!response || response.error) {
            this.notifications.popNotification(
              '',
              this.translationService.getTranslatedContent(
                'error_updating_cogntio_domain._you_may_experience_issues_with_logging_in_with_sso._please_disable_all_identity_providers_at_this_time_as_a_precaution.',
                'Error Updating Cogntio Domain. You may experience issues with logging in with SSO. Please disable all identity providers at this time as a precaution.'
              )
            );
          }
        })
      )
      .subscribe();
  }

  getCogntioDomainNetworkRequest() {
    return this.http
      .request(
        'GET',
        this.environmentService.environment.apiRoot + '/cognito-domain'
      )
      .pipe(
        catchError(error => of({ error })),
        tap((response: any) => {
          this.hasAttemptedCognitoDomain = true;
          const nextDomain =
            !response || response?.error || typeof response !== 'string'
              ? null
              : response;
          // A failed refetch must not erase a domain we already have:
          // /sso/logout needs it after cookies are cleared, and
          // hasAttemptedCognitoDomain would block another try.
          if (
            nextDomain == null &&
            this._cognitoDomainState.value?.cognitoDomain
          ) {
            return;
          }
          this._cognitoDomainState.next({
            isLoading: false,
            cognitoDomain: nextDomain,
          });
        })
      );
  }

  /**
   * Prefer a domain already in memory; only hit the network when we have none.
   * Used on logout so a flaky refetch cannot wipe the value /sso/logout needs.
   */
  ensureCognitoDomain$() {
    const cached = this._cognitoDomainState.value?.cognitoDomain;
    if (cached) {
      return of(cached);
    }
    return this.getCogntioDomainNetworkRequest();
  }

  getCogntioDomain$() {
    if (!this.hasAttemptedCognitoDomain) {
      this.hasAttemptedCognitoDomain = true;
      this.getCogntioDomainNetworkRequest().subscribe();
    }
    return this._cognitoDomainState.asObservable();
  }
}
